import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useMemo } from 'react';
import useSWR from 'swr';
import { tenants } from '@/api/platform';
import { redactMiddle } from '@/utils/format';
import type { Tenant } from '@/types/backend';

/**
 * Reusable tenant picker. Sourced once per session from /admin/tenants
 * (cached 30s) so every platform-admin filter and form shows the same
 * canonical list — no more pasting raw UUIDs.
 *
 * Backed by MUI {@link Autocomplete}, so the operator gets free-text
 * search across organizationName + tenantId, a clear (×) button to
 * reset to "all tenants", and a virtualised scrolling list for the
 * long tail when many tenants are onboarded.
 *
 * Falls back to a free-text input when /admin/tenants errors (some
 * demo deploys hide the list endpoint), so the host page is never
 * bricked.
 */
export function TenantSelect({
  value,
  onChange,
  label = 'Tenant',
  required = false,
  disabled = false,
  size = 'small',
  fullWidth = false,
  allowAll = true,
  helperText,
  sx,
}: {
  value: string;
  onChange: (next: string) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  /** When true, the picker is clearable (×) — equivalent to "All tenants". Set false on required pickers. */
  allowAll?: boolean;
  helperText?: string;
  sx?: object;
}) {
  const { data, error } = useSWR('/admin/tenants/picker', () => tenants.list(), {
    dedupingInterval: 30_000,
  });

  const options = useMemo(() => data ?? [], [data]);
  const selected = useMemo(
    () => options.find((t) => t.tenantId === value) ?? null,
    [options, value],
  );

  if (error) {
    return (
      <TextField
        size={size}
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="paste tenantId manually"
        required={required}
        disabled={disabled}
        fullWidth={fullWidth}
        sx={{ minWidth: 260, ...sx }}
        InputProps={{
          startAdornment: <SearchIcon sx={{ fontSize: 18, mr: 1, color: 'text.tertiary' }} />,
        }}
        helperText={helperText ?? 'Tenant list unavailable — type a tenantId manually.'}
      />
    );
  }

  return (
    <Autocomplete<Tenant, false, boolean, false>
      size={size}
      disabled={disabled}
      fullWidth={fullWidth}
      sx={{ minWidth: 360, ...sx }}
      options={options}
      value={selected}
      onChange={(_, next) => onChange(next?.tenantId ?? '')}
      // Clear is what "All tenants" maps to — only show it when allowed.
      disableClearable={!allowAll}
      autoHighlight
      openOnFocus
      filterOptions={(opts, state) => {
        const lc = state.inputValue.trim().toLowerCase();
        if (!lc) return opts;
        return opts.filter(
          (o) =>
            (o.organizationName ?? '').toLowerCase().includes(lc) ||
            o.tenantId.toLowerCase().includes(lc),
        );
      }}
      getOptionLabel={(o) => o.organizationName ?? o.tenantId}
      isOptionEqualToValue={(a, b) => a.tenantId === b.tenantId}
      noOptionsText="No tenants match"
      // Cap the dropdown height so the list scrolls when there are many
      // tenants — but tall enough that ~12 rows are visible without scroll.
      slotProps={{
        listbox: { sx: { maxHeight: 480 } },
        // Let the popper grow wider than the input so long org names + the
        // mono tenantId stack underneath aren't truncated.
        popper: { sx: { '& .MuiAutocomplete-paper': { minWidth: 380 } } },
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.tenantId} sx={{ py: 0.75 }}>
          <Stack spacing={0} sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }} noWrap>
              {option.organizationName ?? '—'}
            </Typography>
            <Typography
              variant="caption"
              color="text.tertiary"
              sx={{ fontFamily: 'JetBrains Mono, monospace' }}
            >
              {redactMiddle(option.tenantId, 8, 8)}
            </Typography>
          </Stack>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          helperText={helperText}
          placeholder={allowAll ? 'All tenants' : 'Pick a tenant…'}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <SearchIcon sx={{ fontSize: 18, mr: 1, color: 'text.tertiary' }} />
                {params.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
